import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users2, Search, SortAsc, SortDesc, UserX, Loader2 } from "lucide-react";
import { useUsers } from "./users/useUsers";
import { UserCard } from "./users/UserCard";
import { UserRole } from "./users/types";
import { useQueryClient } from "@tanstack/react-query";

export const UsersManagement = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const queryClient = useQueryClient();
  
  const { users, isLoading, isError, updateRole } = useUsers(sortOrder);

  const filteredUsers = users.filter((user) => 
    user.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleUpdateRole = (userId: string, role: UserRole) => {
    updateRole.mutate({ userId, role });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[200px] space-y-4">
        <div className="text-destructive">حدث خطأ أثناء تحميل المستخدمين</div>
        <Button 
          variant="outline"
          onClick={() => queryClient.invalidateQueries({ queryKey: ['admin-users'] })}
        >
          إعادة المحاولة
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Users2 className="h-6 w-6" />
          إدارة المستخدمين
        </h2>
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="بحث عن مستخدم..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-[200px]"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setSortOrder(prev => prev === "asc" ? "desc" : "asc")}
            className="flex items-center gap-2"
          >
            {sortOrder === "asc" ? (
              <SortAsc className="h-4 w-4" />
            ) : (
              <SortDesc className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      <div className="grid gap-4">
        {filteredUsers.map((user) => (
          <UserCard 
            key={user.id} 
            user={user}
            onUpdateRole={handleUpdateRole}
          />
        ))}

        {filteredUsers.length === 0 && (
          <div className="flex flex-col items-center justify-center min-h-[200px] space-y-4">
            <UserX className="h-12 w-12 text-muted-foreground" />
            <div className="text-muted-foreground">لا يوجد مستخدمين مطابقين للبحث</div>
          </div>
        )}
      </div>
    </div>
  );
};